import React from "react"
import { motion } from "framer-motion"
import { theme } from "../settings/config"
import ContentPill from "../atoms/ContentPill"
import { SchemaThumbnail } from "../lesson/schema/ExerciseSchema"
import {
	chosenAreas,
	normalizeLessonContent,
	playerStyleLabel,
	splitPhases,
} from "../utils/lessonContent.utils"
import {
	NOTEBOOK_EXERCISE,
	entryAt,
	exercisePhaseLabel,
	ownerLabel,
	usageLabel,
} from "../utils/notebook.utils"
import { LevelMark, levelPill } from "./NotebookVariants"

// Una voce del quaderno: il nome, cosa c'è dentro, quante volte è stata
// usata e l'ultima. Di una lezione l'obiettivo e le pastiglie, di un
// esercizio la fase e il testo, e accanto lo schema se ce l'ha. Si tocca tutta: nel quaderno la apre,
// dalla lezione la prende.
//
// Con più varianti, sotto ci sono i loro livelli: quella scelta (piena) è
// quella che si prende toccando la scheda, toccando un'altra si prende
// quella. Accanto al nome, di chi è.
function NotebookEntryCard({ entry: listed, schoolID, onClick, onPickVariant }) {
	const entry = entryAt(listed, listed.variantID)
	const variants = listed.variants ?? []
	const isExercise = entry.kind === NOTEBOOK_EXERCISE
	const content = isExercise ? null : normalizeLessonContent(entry.content)
	const pills = content
		? [
				...(content.playerStyle
					? [
							{
								key: content.playerStyle,
								text: playerStyleLabel(content.playerStyle),
								kind: "playerStyle",
							},
						]
					: []),
				...chosenAreas(content).map(({ area, detail }) => ({
					key: area.id,
					text: area.label,
					detail,
					kind: area.list === "situations" ? "situation" : "stroke",
				})),
			]
		: []
	const phases = content ? splitPhases(content).written : []

	return (
		<div
			style={{
				flex: "none",
				borderRadius: "20px",
				background: theme.colors.surface,
				boxShadow: theme.shadows.accent,
			}}>
			<motion.button
				type="button"
				whileTap={{ scale: 0.98 }}
				onClick={onClick}
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "stretch",
					gap: "0.5rem",
					width: "100%",
					textAlign: "left",
					border: 0,
					borderRadius: "20px",
					padding: "0.875rem 1rem",
					background: "transparent",
					color: theme.colors.text,
					font: "inherit",
					cursor: "pointer",
				}}>
				<span
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "baseline",
						gap: "0.5rem",
					}}>
					<span style={{ fontWeight: 700, fontSize: "1rem", color: theme.colors.gunmetal }}>
						{entry.title}
					</span>
					<span style={{ flex: 1 }} />
					<span style={{ flex: "none", fontSize: "0.75rem", color: theme.colors.text_light }}>
						{ownerLabel(entry, schoolID)}
					</span>
					{isExercise && (
						<span
							style={{
								flex: "none",
								fontSize: "0.75rem",
								fontWeight: 600,
								color: theme.colors.gunmetal,
								background: theme.colors.gunmetal_lighter,
								borderRadius: "10px",
								padding: "0.125rem 0.5rem",
							}}>
							{exercisePhaseLabel(entry.phase)}
						</span>
					)}
				</span>
				{content?.objective && (
					<span style={{ fontSize: "0.8125rem", color: theme.colors.gunmetal, fontStyle: "italic" }}>
						{content.objective}
					</span>
				)}
				{pills.length > 0 && (
					<span style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
						{pills.map(({ key, text, detail, kind }) => (
							<ContentPill key={key} kind={kind} detail={detail}>
								{text}
							</ContentPill>
						))}
					</span>
				)}
				{phases.length > 0 && (
					<span style={{ fontSize: "0.75rem", color: theme.colors.text_light }}>
						{phases.map(phase => phase.label).join(" · ")}
					</span>
				)}
				{isExercise && (entry.text || entry.schema) && (
					<span style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
						<span
							style={{
								flex: 1,
								minWidth: 0,
								fontSize: "0.8125rem",
								lineHeight: 1.4,
								display: "-webkit-box",
								WebkitLineClamp: entry.schema ? 4 : 2,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
							}}>
							{entry.text}
						</span>
						{entry.schema && (
							<SchemaThumbnail
								schema={entry.schema}
								width="5rem"
								height="5.5rem"
							/>
						)}
					</span>
				)}
				<span style={{ fontSize: "0.75rem", color: theme.colors.text_light }}>
					{usageLabel(entry)}
				</span>
			</motion.button>
			{onPickVariant && (variants.length > 1 || variants[0]?.level) && (
				<div
					role="group"
					aria-label={`Varianti di ${entry.title}`}
					style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem", padding: "0 1rem 0.875rem" }}>
					{variants.map(variant => {
						const isSelected = variant.variantID === entry.variantID
						return (
							<button
								key={variant.variantID}
								type="button"
								aria-pressed={isSelected}
								onClick={() => onPickVariant(variant.variantID)}
								style={{ ...levelPill(isSelected, theme.colors.background), height: "1.875rem" }}>
								<LevelMark levelID={variant.level} />
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default NotebookEntryCard
